/**
 * @file catalog-category-tree.ts
 * @description Дерево категорий каталога одним модулем (phase19.31, зона
 * «Товары»): построение дерева, подписи без эмодзи, пути «Родитель › Дочь»,
 * потомки узла и число товаров (своё и с подкатегориями — с сервера,
 * `productsCountTotal`, контракт §20; кабинет его больше не пересчитывает
 * по загруженным товарам).
 *
 * Раньше дерево строили три места (`CategoryTreePage`, `CategoryTreeSidebar`,
 * фильтр реестра) — каждое своей копией. Теперь одна функция: выбор в фильтре
 * и строка в «Категориях» не расходятся по порядку и названиям.
 *
 * Эмодзи (SC29): системные корни приходят с сервера с эмодзи в названии
 * («🛍️ Товары витрины»). Подпись для кабинета чистится здесь, исходное
 * название в данных остаётся (его меняет сервер, см. отчёт phase19.31).
 */

import type { CatalogCategory } from "./api-catalog";

/** Узел дерева: категория + глубина + дети (отсортированы). */
export interface CategoryTreeNode extends CatalogCategory {
  depth: number;
  children: CategoryTreeNode[];
}

/** Эмодзи, модификаторы и соединители — всё, что не текст подписи. */
const EMOJI = /\p{Extended_Pictographic}|\p{Regional_Indicator}|\u{FE0F}|\u{200D}|\u{20E3}/gu;
/** Буквы-эмодзи вида «🅰️» (Enclosed Alphanumeric Supplement). */
const ENCLOSED = /[\u{1F100}-\u{1F1FF}]/gu;

/** Название категории без эмодзи: «🛍️ Товары витрины» → «Товары витрины». */
export function categoryLabel(category: Pick<CatalogCategory, "displayName">): string {
  const clean = category.displayName.replace(EMOJI, "").replace(ENCLOSED, "").replace(/\s+/g, " ").trim();
  return clean || category.displayName;
}

/** Порядок соседей: `sortOrder`, затем по алфавиту. */
function compareCategories(a: CatalogCategory, b: CatalogCategory): number {
  return a.sortOrder - b.sortOrder || categoryLabel(a).localeCompare(categoryLabel(b), "ru");
}

/** Список категорий → дерево (сироты становятся корнями). */
export function buildCategoryTree(categories: CatalogCategory[]): CategoryTreeNode[] {
  const byId = new Map<string, CategoryTreeNode>();
  for (const c of categories) byId.set(c.id, { ...c, depth: 0, children: [] });
  const roots: CategoryTreeNode[] = [];
  for (const node of byId.values()) {
    const parent = node.parentId ? byId.get(node.parentId) : undefined;
    if (parent) parent.children.push(node);
    else roots.push(node);
  }
  const sortLevel = (nodes: CategoryTreeNode[], depth: number) => {
    nodes.sort(compareCategories);
    for (const n of nodes) {
      n.depth = depth;
      sortLevel(n.children, depth + 1);
    }
  };
  sortLevel(roots, 0);
  return roots;
}

/** Дерево → плоский список в порядке обхода (для выпадающего списка). */
export function flattenCategoryTree(roots: CategoryTreeNode[]): CategoryTreeNode[] {
  const out: CategoryTreeNode[] = [];
  const walk = (nodes: CategoryTreeNode[]) => {
    for (const n of nodes) {
      out.push(n);
      walk(n.children);
    }
  };
  walk(roots);
  return out;
}

/** Путь категории подписью: «Товары витрины › Чаи и сборы». */
export function categoryPath(categoryId: string, byId: Map<string, CatalogCategory>): string {
  const parts: string[] = [];
  const seen = new Set<string>();
  let cursor: string | null = categoryId;
  while (cursor && !seen.has(cursor)) {
    seen.add(cursor);
    const node = byId.get(cursor);
    if (!node) break;
    parts.unshift(categoryLabel(node));
    cursor = node.parentId;
  }
  return parts.join(" › ");
}

/** Сама категория и все её потомки (для фильтра «Категория» реестра). */
export function categoryWithDescendants(categories: CatalogCategory[], categoryId: string): Set<string> {
  const children = new Map<string, string[]>();
  for (const c of categories) {
    if (!c.parentId) continue;
    children.set(c.parentId, [...(children.get(c.parentId) ?? []), c.id]);
  }
  const out = new Set<string>();
  const stack = [categoryId];
  while (stack.length > 0) {
    const id = stack.pop() as string;
    if (out.has(id)) continue;
    out.add(id);
    stack.push(...(children.get(id) ?? []));
  }
  return out;
}

/**
 * Число товаров категории (контракт §20): `total` — вместе со всеми
 * подкатегориями (`productsCountTotal` сервера), `own` — в самой категории
 * (`productsCount`, только товары магазина). Сервер без нового поля — итог
 * равен своему числу. Не пришло ничего — `null`.
 */
export function categoryProductCounts(
  category: Pick<CatalogCategory, "productsCount" | "productsCountTotal">,
): { total: number; own: number } | null {
  const own = category.productsCount;
  const total = category.productsCountTotal ?? own;
  if (total === undefined) return null;
  return { total, own: own ?? 0 };
}

/**
 * Подсказка к числу, когда в подкатегориях есть товары: «Всего с
 * подкатегориями: 12, в самой категории: 3». Без разницы — `null`.
 */
export function categoryCountHint(counts: { total: number; own: number } | null): string | null {
  if (!counts || counts.total === counts.own) return null;
  return `Всего с подкатегориями: ${counts.total}, в самой категории: ${counts.own}`;
}

/**
 * Действующая публикация категории на сайте, с подъёмом по дереву
 * (catalog-taxonomy.md «Логика публикации»): `null` — как у родителя; на
 * корне без значения — «не публиковать» (безопасное умолчание).
 */
export function effectivePublishOnSiteForCategory(categoryId: string, byId: Map<string, CatalogCategory>): boolean {
  const visited = new Set<string>();
  let cursor: string | null = categoryId;
  while (cursor) {
    if (visited.has(cursor)) return false;
    visited.add(cursor);
    const node = byId.get(cursor);
    if (!node) return false;
    if (node.isPublishedOnSite !== null) return node.isPublishedOnSite;
    cursor = node.parentId;
  }
  return false;
}

/** Пункт выбора категории (совместим с `ComboboxOption`). */
export interface CategoryOption {
  value: string;
  label: string;
  description?: string;
  group: string;
}

/**
 * Дерево категорий пунктами выпадающего списка: корень — заголовок группы,
 * у вложенных глубже второго уровня — «в „Родитель“», у категорий с
 * обязательной маркировкой — «маркировка „Честный знак“» (вместо «⚠ ЧЗ»).
 */
export function categoryTreeOptions(categories: CatalogCategory[]): CategoryOption[] {
  const byId = new Map(categories.map((c) => [c.id, c]));
  const rootOf = (c: CatalogCategory): CatalogCategory => {
    let cursor = c;
    const seen = new Set<string>();
    while (cursor.parentId && !seen.has(cursor.id)) {
      seen.add(cursor.id);
      const parent = byId.get(cursor.parentId);
      if (!parent) break;
      cursor = parent;
    }
    return cursor;
  };
  return flattenCategoryTree(buildCategoryTree(categories)).map((node) => {
    const parent = node.parentId ? byId.get(node.parentId) : undefined;
    const notes = [
      node.depth > 1 && parent ? `в «${categoryLabel(parent)}»` : null,
      node.markingEnabled ? "маркировка «Честный знак»" : null,
    ].filter(Boolean);
    return {
      value: node.id,
      label: categoryLabel(node),
      description: notes.length > 0 ? notes.join(" · ") : undefined,
      group: categoryLabel(rootOf(node)),
    };
  });
}

/**
 * phase19.16 Р2а: опубликованные на сайте категории магазина деревом —
 * «Показать: …» над канвой каталога в конструкторе (значение — адрес
 * категории, отступ «— » — уровень вложенности).
 */
export function publishedCategoryOptions(categories: CatalogCategory[]): Array<{ value: string; label: string }> {
  const byId = new Map(categories.map((c) => [c.id, c]));
  const own = categories.filter((c) => c.isActive && !c.isExternal && !c.isSystemRoot && effectivePublishOnSiteForCategory(c.id, byId));
  return flattenCategoryTree(buildCategoryTree(own)).map((node) => ({ value: node.slug, label: `${"— ".repeat(node.depth)}${categoryLabel(node)}` }));
}
